import React, { useState, useEffect, useRef } from 'react';
import { FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi';

const QuestionTemplateManager = ({
  title,
  description,
  // New prop: Allows external component to manage the template data
  initialTemplate,
  onTemplateUpdate,
  // Original props
  getTemplateFn,
  saveTemplateFn,
  getSystemStatusFn,
  defaultTemplate,
  // New prop
  hideSaveButton = false, // Default to false for normal use
}) => {
  // Use internal state or external state provided by props
  const [template, setTemplateInternal] = useState(initialTemplate || []);
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const prevTemplateType = useRef({});

  // Unified function to update state and notify parent
  const setTemplate = (newTemplate) => {
    setTemplateInternal(newTemplate);
    if (onTemplateUpdate) {
      onTemplateUpdate(newTemplate);
    }
  };

  // --- Load template (Only runs if `initialTemplate` is NOT provided) ---
  useEffect(() => {
    // Only run the loading logic if NOT passed an initial template
    if (!initialTemplate) {
      const loadTemplate = async () => {
        try {
          const status = await getSystemStatusFn();
          setStatusMessage(status.message || '');

          if (status.database_status !== 'OK') {
            setDbError(true);
            setLoading(false);
            return;
          }

          const data = (await getTemplateFn()) || defaultTemplate || [];

          // Populate the ref with original types before mapping
          const originalTypes = {};
          data.forEach(q => {
            originalTypes[q.id] = q.type;
          });
          prevTemplateType.current = originalTypes;

          const initial = data.map(q => ({
            ...q,
            change_type: q.change_type || 'unchanged'
          }));

          setTemplate(initial);

        } catch (error) {
          console.error("Error loading template:", error);
          setStatusMessage(error.message);
          setDbError(true);
        } finally {
          setLoading(false);
        }
      };

      loadTemplate();
    } else {
      // if initialTemplate is provided, skip API load and just use the prop
      setLoading(false);
      setTemplate(initialTemplate);
    }
  }, [getTemplateFn, getSystemStatusFn, defaultTemplate, initialTemplate]);


  // --- Add new question/heading ---
  const addQuestion = (type = 'Text') => {
    const newQ = {
      id: -(Date.now()),
      type,
      text: '',
      mandatory: type === 'Heading' ? false : false,
      scale: type === 'Rating' ? 5 : null,
      change_type: 'new',
    };
    setTemplate([...template, newQ]);
  };

  // --- Set the change mode explicitly from the dropdown ---
  const setChangeMode = (index, mode) => {
    setTemplate(prev => {
      const updated = [...prev];
      updated[index].change_type = mode;
      return updated;
    });
  };

  // --- Update question ---
  const updateQuestion = (index, field, value) => {
    setTemplate(prev => {
      const updated = [...prev];
      const q = { ...updated[index] };

      // detect text edits
      if (field === 'text') {
        if (q.text !== value) {
          if (q.change_type === 'unchanged' || q.change_type === 'existing') {
            q.change_type = 'edit';
          }
        } else if (q.text === value && (q.change_type === 'edit' || q.change_type === 'new')) {
          q.change_type = q.id > 0 ? 'existing' : 'unchanged';
        }
      }

      // detect type change
      if (field === 'type' && q.type !== value) {
        q.change_type = 'new';
        if (value !== 'Rating') {
          q.scale = null;
        } else {
          q.scale = 5;
        }
      }

      q[field] = value;
      updated[index] = q;
      return updated;
    });
  };

  const removeQuestion = (index) => {
    setTemplate(prev => prev.filter((_, i) => i !== index));
  };

  const moveQuestion = (index, direction) => {
    setTemplate(prev => {
      const updated = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= updated.length) return prev;
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      return updated;
    });
  };

  // --- Handle Save (Only runs if `hideSaveButton` is FALSE) ---
  const handleSave = async () => {
    setLoading(true);
    try {
      const templateToSave = template.map(q => {
        const newQ = { ...q };
        if (newQ.change_type === 'new' && newQ.id < 0) {
          delete newQ.id;
        }
        if (newQ.change_type === 'unchanged' || newQ.change_type === 'existing') {
          delete newQ.change_type;
        }
        return newQ;
      });

      await saveTemplateFn(templateToSave);
      alert(`${title} saved successfully!`);

      // reload the template to get fresh IDs and reset change_type state
      const status = await getSystemStatusFn();
      if (status.database_status === 'OK') {
        const reloadedData = (await getTemplateFn()) || defaultTemplate || [];
        const reloadedTemplate = reloadedData.map(q => ({
          ...q,
          change_type: q.change_type || 'unchanged'
        }));
        setTemplate(reloadedTemplate);
      }

    } catch (error) {
      alert(`Failed to save ${title.toLowerCase()}:\n${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-10 text-indigo-500 font-medium">Loading Template...</div>;
  if (dbError)
    return (
      <div className="p-6 bg-red-100 rounded-xl shadow-lg text-red-700 font-medium">
        <p>Database is unreachable. Template editing is disabled.</p>
        {statusMessage && <p className="mt-2 text-sm text-red-800"><strong>Status message:</strong> {statusMessage}</p>}
      </div>
    );

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-4 text-indigo-700">{title}</h2>
      <p className="text-gray-600 mb-6">{description}</p>

      <div className="space-y-3 mb-6">
        {template.map((q, i) => (
          <div
            key={q.id}
            className={`relative flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-lg border gap-2
                          ${q.type === 'Heading' ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-300'}`}
          >
            {/* Question / Heading Input */}
            <div className="flex-1 space-y-1 w-full">
              <input
                type="text"
                value={q.text}
                onChange={(e) => updateQuestion(i, 'text', e.target.value)}
                placeholder={q.type === 'Heading' ? 'Enter section heading...' : 'Enter question text...'}
                className={`w-full p-2 rounded-lg focus:ring-500 focus:border-500
                                  ${q.type === 'Heading' ? 'border-yellow-300 focus:ring-yellow-500 focus:border-yellow-500 font-semibold text-yellow-800' : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'}`}
              />

              {q.type !== 'Heading' && (
                <div className="flex flex-wrap gap-3 mt-1">
                  <select
                    value={q.type}
                    onChange={(e) => updateQuestion(i, 'type', e.target.value)}
                    className="p-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="Text">Free Text</option>
                    <option value="Rating">Scale</option>
                    <option value="Choice">Checkbox</option>
                  </select>

                  {(q.type === 'Rating') && (
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={q.scale || 5}
                      onChange={(e) => updateQuestion(i, 'scale', Number(e.target.value))}
                      className="w-20 p-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Scale"
                    />
                  )}

                  <label className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={q.mandatory}
                      onChange={(e) => updateQuestion(i, 'mandatory', e.target.checked)}
                      disabled={q.type === 'Heading'}
                    />
                    <span className={q.type === 'Heading' ? 'text-gray-400' : ''}>Mandatory</span>
                  </label>

                  {/* hide the change_type dropdown if we are in ad-hoc mode (no existing questions) */}
                  {!hideSaveButton && (q.change_type === 'edit' || q.change_type === 'new') && (
                    <select
                      value={q.change_type}
                      // disable logic relies on prevTemplateType, which will be empty in ad-hoc mode
                      disabled={q.change_type === 'new' && q.id > 0 && q.type !== prevTemplateType.current[q.id]}
                      onChange={(e) => setChangeMode(i, e.target.value)}
                      className={`p-2 border rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 
                      ${(q.change_type === 'new' && q.id > 0 && q.type !== prevTemplateType.current[q.id]) ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : 'bg-white'}`}
                    >
                      <option value="new">New Question</option>
                      <option value="edit">Edit Existing Question</option>
                    </select>
                  )}
                </div>
              )}
            </div>

            {/* Controls (Move/Delete) */}
            <div className="flex flex-col items-center sm:ml-2">
              <button
                onClick={() => moveQuestion(i, 'up')}
                disabled={i === 0}
                className={`text-gray-500 hover:text-indigo-600 ${i === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                title="Move Up"
              >
                <FiArrowUp size={18} />
              </button>
              <button
                onClick={() => moveQuestion(i, 'down')}
                disabled={i === template.length - 1}
                className={`text-gray-500 hover:text-indigo-600 ${i === template.length - 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                title="Move Down"
              >
                <FiArrowDown size={18} />
              </button>
              <button
                onClick={() => removeQuestion(i)}
                className="text-red-500 hover:text-red-700 mt-2"
                title="Remove"
              >
                <FiTrash2 size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-4">
        <button
          onClick={() => addQuestion('Text')}
          className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition font-medium"
        >
          Add New Question
        </button>
        <button
          onClick={() => addQuestion('Heading')}
          className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition font-medium"
        >
          Add Heading
        </button>
      </div>

      {/* hide save button if requested (i.e., in AdHoc mode) */}
      {!hideSaveButton && (
        <button
          onClick={handleSave}
          className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition font-medium"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Template'}
        </button>
      )}
    </div>
  );
};

export default QuestionTemplateManager;